import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

interface VoiceMessageProps {
  audioBlob: Blob;
  duration: number;
  isUser: boolean;
  accentColor: string;
}

const VoiceMessage: React.FC<VoiceMessageProps> = ({
  audioBlob,
  duration,
  isUser,
  accentColor,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [isLoading, setIsLoading] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioUrl = useRef<string | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (audioBlob) {
      audioUrl.current = URL.createObjectURL(audioBlob);
    }

    return () => {
      if (audioUrl.current) {
        URL.revokeObjectURL(audioUrl.current);
      }
    };
  }, [audioBlob]);

  const handlePlayPause = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState < 2) {
          setIsLoading(true);
          await new Promise<void>((resolve) => {
            const handleCanPlay = () => {
              audioRef.current?.removeEventListener('canplay', handleCanPlay);
              resolve();
            };
            audioRef.current?.addEventListener('canplay', handleCanPlay);
          });
          setIsLoading(false);
        }

        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
        setIsLoading(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * audioDuration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage =
    audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className="flex items-center gap-3" style={{ minWidth: '220px', maxWidth: '280px' }}>
      <audio
        ref={audioRef}
        src={audioUrl.current || undefined}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />

      <button
        onClick={handlePlayPause}
        disabled={isLoading}
        className="flex-shrink-0 rounded-full flex items-center justify-center transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: isUser ? '#ffffff30' : accentColor + '30',
          color: isUser ? '#ffffff' : accentColor,
          border: `2px solid ${isUser ? '#ffffff60' : accentColor + '60'}`,
        }}
      >
        {isLoading ? (
          <div
            className="border-2 border-current rounded-full animate-spin"
            style={{
              width: '16px',
              height: '16px',
              borderTopColor: 'transparent',
            }}
          />
        ) : isPlaying ? (
          <Pause className="w-4 h-4" />
        ) : (
          <Play className="w-4 h-4" />
        )}
      </button>

      <div className="flex-1 flex flex-col gap-2">
        {/* Progress bar */}
        <div
          ref={progressRef}
          className="relative h-1 rounded-full overflow-hidden cursor-pointer"
          style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
          onClick={handleProgressClick}
        >
          <div
            className="absolute left-0 top-0 h-full rounded-full transition-all"
            style={{
              width: `${progressPercentage}%`,
              transitionDuration: '100ms',
              backgroundColor:
                currentTime === 0 && !isPlaying
                  ? isUser
                    ? '#ffffff40'
                    : accentColor + '40'
                  : isUser
                  ? '#ffffff'
                  : accentColor,
            }}
          />
        </div>

        <div className="flex justify-between text-xs" style={{ opacity: 0.7 }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(audioDuration)}</span>
        </div>
      </div>
    </div>
  );
};

export default VoiceMessage;
